import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { practiceParMs, practiceRole, type PuzzleType } from '@il-colpo/engine';
import { trainingKey } from '@il-colpo/shared/progress';
import { clock, stars } from '../../game/format.ts';
import { TrainingBoard } from './TrainingBoard.tsx';
import { useLooseT } from '../../game/text/loose.ts';
import { useTrainingStore } from '../../game/trainingStore.ts';
import { useProgressStore } from '../../state/progressStore.ts';
import { useSettings } from '../../state/settingsStore.ts';
import { Badge, Button, Panel, ScreenFrame, SegmentedControl } from '../../ui/index.ts';
import styles from './SoloSetup.module.css';

export const TRAINING_TYPES: readonly PuzzleType[] = [
  'lock',
  'safe',
  'panel',
  'cameras',
  'laser',
  'password',
  'corridor',
  'escape',
  'vault',
];
const LEVELS = [1, 2, 3, 4, 5] as const;

/** Training (GDD §11): pick a puzzle and a level, then play it on its own against the clock. */
export function TrainingSetup() {
  const { t } = useTranslation('training');
  const g = useLooseT('game');
  const navigate = useNavigate();
  const { language } = useSettings();
  const { t: ts } = useTranslation('setup');
  const start = useTrainingStore((s) => s.start);
  const records = useProgressStore((s) => s.progress.training);

  const [type, setType] = useState<PuzzleType>('lock');
  const [level, setLevel] = useState<(typeof LEVELS)[number]>(2);
  const [starting, setStarting] = useState(false);
  const record = records[trainingKey(type, level)];

  const go = async () => {
    setStarting(true);
    await start({ type, level, humanName: ts('you'), language });
    void navigate('/training');
  };

  return (
    <ScreenFrame title={t('title')} backTo="/play" tone="blueprint">
      <div className={styles['form']}>
        <p className={styles['intro']}>{t('intro')}</p>
        <Panel tone="blueprint" className={styles['panel']}>
          <SegmentedControl
            label={t('puzzle')}
            value={type}
            onChange={setType}
            options={TRAINING_TYPES.map((p) => ({ value: p, label: g(`puzzles.${p}.name`) }))}
          />
          <p className={styles['hint']}>{g(`puzzles.${type}.goal`)}</p>
          <SegmentedControl
            label={t('level')}
            value={level}
            onChange={setLevel}
            options={LEVELS.map((n) => ({ value: n, label: t('levelOption', { n }) }))}
          />
          <p className={styles['hint']}>{t('levelHint')}</p>
          <div className={styles['facts']}>
            <Badge>{t('solver', { role: g(`roles.${practiceRole(type)}`) })}</Badge>
            <Badge>{t('par', { time: clock(practiceParMs(type, level)) })}</Badge>
            {record ? (
              <Badge tone="green">
                <span aria-label={t('starsLabel', { count: record.stars })}>
                  {stars(record.stars)}
                </span>
                {` ${t('record', { time: clock(record.bestMs) })}`}
              </Badge>
            ) : (
              <Badge>{t('noRecord')}</Badge>
            )}
          </div>
        </Panel>
        <Button size="lg" icon="play" loading={starting} onClick={() => void go()} block>
          {t('start')}
        </Button>
        <TrainingBoard puzzle={type} level={level} />
      </div>
    </ScreenFrame>
  );
}
